import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useLanguage } from "../../context/LanguageContext";
import LanguageDropdown from "../common/LanguageDropdown";
import Icon from "../common/Icon";
import styles from "./Header.module.css";

import logo from "../../assets/images/logo-rewind.png";
import login from "../../assets/images/login.png";
import { getAdminInfo } from "../../api";
import { COMPANY_ADDONS, hasAddon, hasAddonOrOwner } from "../../utils/addons";
import { User } from "../../types/types";

const Header: React.FC = () => {
    const [user, setUser] = useState<User | null>(null);
    const { t: rawT, i18n } = useTranslation();
    const t = rawT as any;
    const { switchLanguage } = useLanguage();
    const [isCreateDropdownVisible, setCreateDropdownVisible] = useState(false);
    const [isResourcesDropdownVisible, setResourcesDropdownVisible] = useState(false);
    const [isPublishDropdownVisible, setPublishDropdownVisible] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const uid = uuidv4();
    const newTourLink = `/create/tour/${uid}/category`;

    const isActive = (path: string) => {
        if (path === "/") {
            return location.pathname === "/";
        }
        return location.pathname.startsWith(path);
    };

    const isCreateActive = () => {
        return location.pathname.startsWith("/create") || location.pathname.startsWith("/chatbots");
    };

    const isDistributeActive = () => {
        return (
            location.pathname.startsWith("/hubs") ||
            location.pathname.startsWith("/manage/audiotracks") ||
            location.pathname.startsWith("/notification") ||
            location.pathname.startsWith("/activity") ||
            location.pathname.startsWith("/analytics") ||
            location.pathname.startsWith("/reviews")
        );
    };

    const isResourcesActive = () => {
        return location.pathname.startsWith("/database") || location.pathname.startsWith("/resources");
    };

    const fetchUser = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            setUser(null);
            return;
        }

        try {
            const response = await getAdminInfo({ token });
            if (!response) {
                console.error("Error fetching user info");
            } else {
                setUser({
                    ...response,
                    company: response.cmpany?.name || "",
                });
            }
        } catch (error) {
            console.error("Error fetching user:", error);
        }
    };

    useEffect(() => {
        const savedLang = localStorage.getItem("appLanguage");
        if (savedLang && savedLang !== i18n.language) {
            switchLanguage(savedLang.toUpperCase());
            i18n.changeLanguage(savedLang);
        }
        fetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSwitchLanguage = (lang: string) => {
        switchLanguage(lang.toUpperCase());
        i18n.changeLanguage(lang);
        localStorage.setItem("appLanguage", lang);
    };

    const userCompanyAddons = user && user.cmpany && Array.isArray(user.cmpany.addons) ? user.cmpany.addons : [];
    const hasAccessToNotifications = userCompanyAddons.includes("notifications");
    const hasAccessToActivities = userCompanyAddons.includes("activities");
    const showPublishAnalyticsLink = hasAddonOrOwner(user, COMPANY_ADDONS.ANALYTICS);
    const showPublishReviewsLink = hasAddonOrOwner(user, COMPANY_ADDONS.REVIEWS);
    const showPublishExportUsersLink = Boolean(user?.cmpany && hasAddon(user.cmpany, COMPANY_ADDONS.EXPORTUSERS));
    const hasPublishActivityTrackingSection =
        showPublishAnalyticsLink || showPublishReviewsLink || showPublishExportUsersLink;

    const linkClass = (active: boolean) => `${styles.navLink}${active ? ` ${styles.navLinkActive}` : ""}`;

    return (
        <header className={styles.header}>
            <Link to="/" className={styles.brand}>
                <img src={logo} alt="Rewind" className={styles.brandLogo} />
            </Link>

            <ul className={styles.navLinks}>
                <li className={styles.navItem}>
                    <Link to="/" className={linkClass(isActive("/"))}>
                        {t("home")}
                    </Link>
                </li>

                <li
                    className={styles.navItem}
                    onMouseEnter={() => setResourcesDropdownVisible(true)}
                    onMouseLeave={() => setResourcesDropdownVisible(false)}
                >
                    <button type="button" className={linkClass(isResourcesActive())}>
                        {t("resources")}
                        <Icon name="ic-chevron-down" />
                    </button>
                    {isResourcesDropdownVisible && (
                        <ul className={styles.dropdown}>
                            <li className={styles.dropdownTitle}>
                                <span className={`${styles.dropdownChip} ${styles.dropdownChipGreen}`}>
                                    <Icon name="ic-library" />
                                </span>
                                {t("resourcesSectionTitle")}
                            </li>
                            {user?.cmpany && hasAddon(user?.cmpany, COMPANY_ADDONS.DATABASE) && (
                                <li>
                                    <Link to="/database">{t("database")}</Link>
                                </li>
                            )}
                            <li>
                                <Link to="/resources/brand-voices">{t("brandVoices")}</Link>
                            </li>
                            <li>
                                <Link to="/resources/audience-profiles">{t("audienceProfiles")}</Link>
                            </li>
                        </ul>
                    )}
                </li>

                <li
                    className={styles.navItem}
                    onMouseEnter={() => setCreateDropdownVisible(true)}
                    onMouseLeave={() => setCreateDropdownVisible(false)}
                >
                    <button type="button" className={linkClass(isCreateActive())}>
                        {t("create")}
                        <Icon name="ic-chevron-down" />
                    </button>
                    {isCreateDropdownVisible && (
                        <ul className={styles.dropdown}>
                            <li className={styles.dropdownTitle}>
                                <span className={`${styles.dropdownChip} ${styles.dropdownChipGreen}`}>
                                    <Icon name="ic-sparkles" />
                                </span>
                                {t("createContent")}
                            </li>
                            <li>
                                <Link to="/create/text">{t("audioCapsule")}</Link>
                            </li>
                            {user?.isOwner && (
                                <li>
                                    <Link to="/create/cartel">{t("cartel")}</Link>
                                </li>
                            )}
                            {user?.isOwner && (
                                <li>
                                    <Link to="/create/newsletter">{t("newsletter")}</Link>
                                </li>
                            )}
                            {user?.isOwner && (
                                <li>
                                    <Link to="/create/promotional-text">{t("promotionalContent")}</Link>
                                </li>
                            )}
                            {user?.cmpany && hasAddon(user.cmpany, COMPANY_ADDONS.CHATBOT) && (
                                <li>
                                    <Link to="/chatbots">{t("chatbot")}</Link>
                                </li>
                            )}
                        </ul>
                    )}
                </li>

                <li
                    className={styles.navItem}
                    onMouseEnter={() => setPublishDropdownVisible(true)}
                    onMouseLeave={() => setPublishDropdownVisible(false)}
                >
                    <button type="button" className={linkClass(isDistributeActive())}>
                        {t("distribute")}
                        <Icon name="ic-chevron-down" />
                    </button>
                    {isPublishDropdownVisible && (
                        <ul className={styles.dropdown}>
                            {user?.cmpany && hasAddon(user.cmpany, COMPANY_ADDONS.AUDIOPWA) && (
                                <li className={styles.dropdownTitle}>
                                    <span className={`${styles.dropdownChip} ${styles.dropdownChipGreen}`}>
                                        <Icon name="ic-globe" />
                                    </span>
                                    {t("hubsWebapp")}
                                </li>
                            )}
                            {user?.cmpany && hasAddon(user.cmpany, COMPANY_ADDONS.AUDIOPWA) && (
                                <li>
                                    <Link to="/hubs/create">{t("createHub")}</Link>
                                </li>
                            )}
                            {user?.cmpany && hasAddon(user.cmpany, COMPANY_ADDONS.AUDIOPWA) && (
                                <li>
                                    <Link to="/hubs">{t("myHubs")}</Link>
                                </li>
                            )}
                            <li className={styles.dropdownTitle}>
                                <span className={`${styles.dropdownChip} ${styles.dropdownChipBlue}`}>
                                    <Icon name="ic-smartphone" />
                                </span>
                                {t("rewindApp")}
                            </li>
                            <li>
                                <Link to={newTourLink}>{t("createAudioguide")}</Link>
                            </li>
                            <li>
                                <Link to="/manage/audiotracks">{t("myAudioguides")}</Link>
                            </li>
                            {hasAccessToNotifications && (
                                <li>
                                    <Link to="/notification">{t("notification")}</Link>
                                </li>
                            )}
                            {hasAccessToActivities && (
                                <li>
                                    <Link to="/activity">{t("activity")}</Link>
                                </li>
                            )}
                            {hasPublishActivityTrackingSection && (
                                <>
                                    <li className={styles.dropdownTitle}>
                                        <span className={`${styles.dropdownChip} ${styles.dropdownChipAmber}`}>
                                            <Icon name="ic-bar-chart" />
                                        </span>
                                        {t("distributeActivityTracking")}
                                    </li>
                                    {showPublishAnalyticsLink && (
                                        <li>
                                            <Link to="/analytics">{t("analytics")}</Link>
                                        </li>
                                    )}
                                    {showPublishReviewsLink && (
                                        <li>
                                            <Link to="/reviews">{t("reviews")}</Link>
                                        </li>
                                    )}
                                    {showPublishExportUsersLink && (
                                        <li>
                                            <Link to="/analytics/export-users">{t("exportUsers")}</Link>
                                        </li>
                                    )}
                                </>
                            )}
                        </ul>
                    )}
                </li>

                {user?.isOwner && (
                    <li className={styles.navItem}>
                        <Link to="/administration" className={linkClass(isActive("/administration"))}>
                            {t("administration")}
                        </Link>
                    </li>
                )}
                {user?.isOwner && (
                    <li className={styles.navItem}>
                        <Link to="/support" className={linkClass(isActive("/support"))}>
                            {t("support")}
                        </Link>
                    </li>
                )}
                <li className={styles.navItem}>
                    <Link to="/documentation" className={linkClass(isActive("/documentation"))}>
                        {t("documentation")}
                    </Link>
                </li>
            </ul>

            <div className={styles.navRight}>
                <LanguageDropdown value={i18n.language} onChange={handleSwitchLanguage} />
                <button type="button" className={styles.avatarBtn} onClick={() => navigate("/profile")}>
                    <img src={login} alt="Profile" />
                </button>
            </div>
        </header>
    );
};

export default Header;
